import { MODEL_SCORE_TIERS } from "@/lib/scoring/scoreTone";

/**
 * Tier model for the Market Sizing tab.
 *
 * V2 (argo) models carry their own A–F/DQ tier thresholds server-side, so the
 * per-profile tab lets the user multi-select among those canonical tiers. V1
 * (niffler) profiles expose a 0–100 fit score with no canonical letter tiers,
 * so their tab maps fixed score bands to letters instead (single-select,
 * mirrors the existing V1 scoring filter elsewhere in the app).
 */

export interface TierBand {
  min: number;
  max: number;
}

/** Fit-score bands used to bucket a market scored by a V1 profile. */
export const V1_TIER_BANDS: Record<string, TierBand> = {
  A: { min: 75, max: 100 },
  B: { min: 55, max: 74 },
  C: { min: 35, max: 54 },
  D: { min: 15, max: 34 },
  F: { min: 0, max: 14 },
};

export const V1_TIERS = ["A", "B", "C", "D", "F"] as const;

/** Canonical V2 tiers, in display order. DQ means "disqualified — not
 * graded". */
export const V2_TIERS = MODEL_SCORE_TIERS;

/** Tier ramp: strongest tiers darkest, weakest lightest, DQ neutral. */
export const TIER_COLORS: Record<string, string> = {
  A: "#16325c",
  B: "#1f4e96",
  C: "#3d6fc0",
  D: "#7d9fd7",
  E: "#a3bce4",
  F: "#c3d3ec",
  DQ: "#9e9e9e",
};

/** Text color for each tier's background above — D/E/F/DQ are too light for
 * white text to clear the 4.5:1 contrast ratio, so they get dark text
 * instead; A/B/C stay white on their darker backgrounds. */
export const TIER_TEXT_COLORS: Record<string, string> = {
  A: "#fff",
  B: "#fff",
  C: "#fff",
  D: "#212121",
  E: "#212121",
  F: "#212121",
  DQ: "#212121",
};

/** What each tier means for go-to-market motion, shown on the tier cards. */
export const TIER_COMMENTARY: Record<string, string> = {
  A: "Immediate sales action for current quarter close",
  B: "Immediate sales action for current half year close",
  C: "Immediate sales & marketing nurture for fiscal close",
  D: "Sales & marketing nurture to develop opportunity",
  E: "Long-term nurture — developing propensity",
  F: "Long-term nurture — low propensity",
  DQ: "Disqualified — not graded",
};

/** How one scoring model's tabs name and hide their tiers. Both are per model:
 * an A tier means something different under each model, so a team that calls
 * it "Champions" for one and hides it entirely for another is describing two
 * different things, not contradicting itself. */
export interface TierSettings {
  /** Tier letter -> custom name. A tier with no entry here (or an empty one)
   * shows its letter alone. */
  names: Record<string, string>;
  /** Tier letters this model doesn't show — gone from the tier cards, the
   * top-accounts table, the exported report and the public share page. */
  hidden: string[];
}

/** Keyed exactly like SpendCategoriesByModel below. A model with no entry
 * starts with every tier visible and unnamed. */
export type TierSettingsByModel = Record<string, TierSettings>;

export const TIER_SETTINGS_BY_MODEL_STORAGE_KEY =
  "marketSizing.tierSettingsByModel";

export const EMPTY_TIER_SETTINGS: TierSettings = { names: {}, hidden: [] };

/** One spend-category selection, scoped to a single scoring model's tab. */
export interface SpendCategorySelection {
  ids: string[];
  /** id -> applicability weight (0-100). Missing entries count in full (100%). */
  weights: Record<string, number>;
}

/** Keyed by a V2 model's family, or DEFAULT_MODEL_KEY when no V2 tab is
 * open — each scoring tab keeps its own spend-category selection. A model
 * with no entry starts empty; it never borrows another key's bucket,
 * including "default". */
export type SpendCategoriesByModel = Record<string, SpendCategorySelection>;

export const SPEND_CATEGORIES_BY_MODEL_STORAGE_KEY =
  "marketSizing.spendCategoriesByModel";

/** The bucket used when no V2 scoring tab is open. Shared by the spend
 * categories and the tier settings — both are keyed by the same open tab. */
export const DEFAULT_MODEL_KEY = "default";

export const CORPORATE_PARENT_ONLY_STORAGE_KEY =
  "marketSizing.corporateParentOnly";

/** The scope Market Sizing's settings hang off outside a saved segment. */
export const EXPLORE_SCOPE = "explore";

/** Every Market Sizing setting that is scoped to one segment. */
const SCOPED_STORAGE_KEYS = [
  TIER_SETTINGS_BY_MODEL_STORAGE_KEY,
  SPEND_CATEGORIES_BY_MODEL_STORAGE_KEY,
  CORPORATE_PARENT_ONLY_STORAGE_KEY,
];

/**
 * Carries Market Sizing's settings onto a segment that has just been saved.
 *
 * These are scoped by segment id, so without this everything the user set up
 * while exploring — tier names, hidden tiers, spend categories — would be
 * dropped the moment they saved, which is also the exact moment Share becomes
 * available to them. Saving is meant to keep what is on screen, not reset it.
 *
 * Settings already on the target are left alone: "save as new" onto a segment
 * that has its own configuration is not an invitation to overwrite it.
 */
export function carrySegmentScopedSettings(
  fromScope: string,
  toScope: string
): void {
  if (fromScope === toScope) return;
  for (const key of SCOPED_STORAGE_KEYS) {
    try {
      const value = localStorage.getItem(`${key}.${fromScope}`);
      if (value === null) continue;
      if (localStorage.getItem(`${key}.${toScope}`) !== null) continue;
      localStorage.setItem(`${key}.${toScope}`, value);
    } catch {
      // A full or blocked localStorage costs the user their tier names, not
      // their segment — never let it fail the save that just succeeded.
    }
  }
}

/** Top-N cap for the ranked charts (geography, industry). Fewer rows render
 * when the market has fewer than this many countries/industries with spend. */
export const CHART_TOP_N = 20;

/** The ranked charts carry twice the rows of the default chart area, so they
 * get a taller one — 20 rows at the standard 350px is unreadable. */
export const RANKED_CHART_HEIGHT = 600;

/** A tier's custom name if this segment gave it one, else its letter. Named
 * tiers keep the letter as a prefix so a reader can still line the row up
 * with the colour-coded cards above it. Shared by the live top-accounts panel
 * and the downloaded report, which must label the same tier the same way. */
export function tierLabel(
  tier: string,
  tierNames: Record<string, string>
): string {
  const name = tierNames[tier]?.trim();
  return name ? `${tier} — ${name}` : tier;
}

/** The tiers this model actually shows, in canonical order. Hiding is purely
 * presentational — it removes a tier from view, it never re-bases the "% of
 * market" the remaining cards report, which stays against the whole market so
 * a tier reads the same figure whatever else is hidden beside it. */
export function visibleTiers(
  tiers: readonly string[],
  hidden: readonly string[]
): string[] {
  const hiddenSet = new Set(hidden);
  return tiers.filter((tier) => !hiddenSet.has(tier));
}
